/**
 * Separation: 3 nm laterally or 1 000 ft vertically, both airborne (ATC-SIM standard).
 * With wake separation on, a follower within 1 000 ft below a heavier category and less than
 * the wake distance behind it also counts as a loss (4/5/6 nm behind a heavy for heavy/
 * medium/light followers; 3 nm behind a medium for a light — approximations).
 */
import { bearingDeg, distanceM, KT_TO_MPS, NM_TO_M, wrap180, type AircraftCategory, type AirportData, type RealismOptions } from '@atc/core';
import { independentApproach } from './runways';
import { isOnGroundPhase, isRemovedPhase, stepAircraft, type Wind, type Aircraft } from './aircraft';
import { executeInstruction, recheckInstruction } from './pilot';
import { occupiedRunways, runwayUnable } from './runways';
import type { Instruction } from './commands';

export const LATERAL_MIN_NM = 3;
export const VERTICAL_MIN_FT = 1000;

const WAKE_NM: Record<AircraftCategory, Record<AircraftCategory, number>> = {
  heavy: { heavy: 4, medium: 5, light: 6 },
  medium: { heavy: 0, medium: 0, light: 3 },
  light: { heavy: 0, medium: 0, light: 0 },
};

export interface ConflictPair { a: Aircraft; b: Aircraft; key: string; lateralNm: number; verticalFt: number; wake: boolean }

export function findConflicts(aircraft: Aircraft[], wake: boolean, airport?: AirportData): ConflictPair[] {
  const out: ConflictPair[] = [];
  const air = aircraft.filter((a) => !a.onGround && !isOnGroundPhase(a.phase) && !isRemovedPhase(a.phase));
  for (let i = 0; i < air.length; i++) for (let j = i + 1; j < air.length; j++) {
    const a = air[i]!, b = air[j]!;
    const lateralNm = distanceM(a.lat, a.lon, b.lat, b.lon) / NM_TO_M;
    if (lateralNm >= 6) continue;
    const verticalFt = Math.abs(a.altitudeFt - b.altitudeFt);
    const key = a.id < b.id ? `${a.id}-${b.id}` : `${b.id}-${a.id}`;
    // Two aircraft established on different parallel runways are legally separated (simplification).
    const parallel = independentApproach(airport, a, b);
    if (lateralNm < LATERAL_MIN_NM && verticalFt < VERTICAL_MIN_FT && !parallel) { out.push({ a, b, key, lateralNm, verticalFt, wake: false }); continue; }
    if (!wake || verticalFt >= VERTICAL_MIN_FT) continue;
    // Wake: which one is behind the other along its own track?
    for (const [lead, follow] of [[a, b], [b, a]] as const) {
      const need = WAKE_NM[lead.category][follow.category];
      if (!need || lateralNm >= need) continue;
      const brg = bearingDeg(follow.lat, follow.lon, lead.lat, lead.lon);
      const ahead = Math.abs(wrap180(brg - follow.trackDeg)) < 30;
      const sameDir = Math.abs(wrap180(lead.trackDeg - follow.trackDeg)) < 45;
      if (ahead && sameDir && follow.altitudeFt <= lead.altitudeFt + 100) { out.push({ a: lead, b: follow, key, lateralNm, verticalFt, wake: true }); break; }
    }
  }
  return out;
}

/** How far ahead the short-term conflict alert looks, seconds. */
export const STCA_LOOKAHEAD_S = 60;

export interface PredictedConflict { a: Aircraft; b: Aircraft; key: string; /** Seconds until separation is lost. */ inS: number }

/**
 * Short-term conflict alert: extrapolate each airborne pair in a straight line (track, ground
 * speed, vertical speed levelling off at the cleared altitude), sampled every 5 s up to
 * `lookaheadS`, and report pairs that would come within 3 nm and 1 000 ft. With airport/options
 * provided, use bounded flight-guidance samples including queued clearances instead. Only
 * airport-authored independent approaches are exempt, as in findConflicts.
 */
export interface PendingPrediction { ac: Aircraft; inS: number; instrs: readonly Instruction[] }
export function predictConflicts(aircraft: Aircraft[], lookaheadS = STCA_LOOKAHEAD_S, airport?: AirportData, opts?: RealismOptions, wind: Wind = { directionDeg: 0, speedKt: 0 }, pending: readonly PendingPrediction[] = []): PredictedConflict[] {
  lookaheadS = Math.min(120, Math.max(0, Number.isFinite(lookaheadS) ? lookaheadS : STCA_LOOKAHEAD_S));
  if (airport && opts) return predictFlightPaths(aircraft, lookaheadS, airport, opts, wind, pending);
  const out: PredictedConflict[] = [];
  const air = aircraft.filter((a) => !a.onGround && !isOnGroundPhase(a.phase) && !isRemovedPhase(a.phase));
  const minM = LATERAL_MIN_NM * NM_TO_M;
  for (let i = 0; i < air.length; i++) for (let j = i + 1; j < air.length; j++) {
    const a = air[i]!, b = air[j]!;
    const lateralNm = distanceM(a.lat, a.lon, b.lat, b.lon) / NM_TO_M;
    if (lateralNm > LATERAL_MIN_NM + ((a.groundspeedKt + b.groundspeedKt) * lookaheadS) / 3600) continue;
    if (independentApproach(airport, a, b)) continue;
    // Local flat frame in metres, origin at a.
    const mPerDegLat = 111_320, mPerDegLon = 111_320 * Math.cos((a.lat * Math.PI) / 180);
    const e0 = (b.lon - a.lon) * mPerDegLon, n0 = (b.lat - a.lat) * mPerDegLat;
    const va = velocity(a), vb = velocity(b);
    for (let t = 5; t <= lookaheadS; t += 5) {
      const e = e0 + (vb.e - va.e) * t, n = n0 + (vb.n - va.n) * t;
      if (Math.hypot(e, n) >= minM) continue;
      if (Math.abs(altitudeIn(a, t) - altitudeIn(b, t)) < VERTICAL_MIN_FT) {
        out.push({ a, b, key: a.id < b.id ? `${a.id}-${b.id}` : `${b.id}-${a.id}`, inS: t });
        break;
      }
    }
  }
  return out;
}

function velocity(ac: Aircraft): { e: number; n: number } {
  const v = ac.groundspeedKt * KT_TO_MPS, r = (ac.trackDeg * Math.PI) / 180;
  return { e: v * Math.sin(r), n: v * Math.cos(r) };
}

/** Altitude after t seconds at the current vertical speed, stopping at the cleared altitude (not on a glideslope). */
function altitudeIn(ac: Aircraft, t: number): number {
  const next = ac.altitudeFt + (ac.verticalSpeedFpm * t) / 60;
  if (ac.approach?.onSlope || ac.verticalSpeedFpm === 0) return ac.approach?.onSlope ? next : ac.altitudeFt;
  return ac.verticalSpeedFpm > 0 ? Math.min(next, Math.max(ac.targetAltitudeFt, ac.altitudeFt)) : Math.max(next, Math.min(ac.targetAltitudeFt, ac.altitudeFt));
}

/** Predict commanded turns in a local frame. Does not mutate the sim or emit events. */
function predictFlightPaths(active: Aircraft[], lookaheadS: number, airport: AirportData, opts: RealismOptions, wind: Wind, pending: readonly PendingPrediction[]): PredictedConflict[] {
  const air = active.filter((a) => !a.onGround && !isOnGroundPhase(a.phase) && !isRemovedPhase(a.phase));
  const latScale = 111320, lonScale = latScale * Math.cos(airport.lat * Math.PI / 180);
  const paths = air.map((ac) => {
    const predicted: Aircraft = { ...ac, assigned: { ...ac.assigned }, nav: { ...ac.nav, hold: ac.nav.hold ? { ...ac.nav.hold } : undefined }, approach: ac.approach ? { ...ac.approach, via: ac.approach.via?.slice() } : undefined };
    const queued = pending.filter((p) => p.ac === ac).map((p) => ({ ...p })).sort((a, b) => a.inS - b.inS);
    const ctx = { airport, opts, wind, simTimeS: 0, occupiedRunways: occupiedRunways(airport, active.filter((a) => a !== ac)), emit: () => {} };
    const points: { e: number; n: number; alt: number; heading: number; airborne: boolean; runway?: string }[] = [];
    // Five-second samples bound forecast cost independently of the fixed-step simulation.
    for (let t = 5; t <= lookaheadS; t += 5) {
      while (queued[0] && queued[0].inS <= t) {
        const transmission = queued.shift()!;
        const context = { altitudeInTransmission: transmission.instrs.some((i) => i.kind === 'altitude'), speedInTransmission: transmission.instrs.some((i) => i.kind === 'speed') };
        for (const instr of transmission.instrs) {
          if (recheckInstruction(predicted, instr, airport) || (['land', 'takeoff', 'lineUp'].includes(instr.kind) && runwayUnable(airport, active.filter((a) => a !== ac).concat(predicted), predicted, instr))) continue;
          executeInstruction(predicted, instr, airport, opts, context);
        }
      }
      ctx.simTimeS = t;
      stepAircraft(predicted, 5, ctx);
      points.push({ e: (predicted.lon - airport.lon) * lonScale, n: (predicted.lat - airport.lat) * latScale, alt: predicted.altitudeFt, heading: predicted.trackDeg, airborne: !predicted.onGround && !isRemovedPhase(predicted.phase), runway: predicted.approach?.established ? predicted.approach.runway.name : undefined });
    }
    return points;
  });
  const out: PredictedConflict[] = [];
  for (let i = 0; i < air.length; i++) for (let j = i + 1; j < air.length; j++) {
    const a = air[i]!, b = air[j]!;
    for (let k = 0; k < paths[i]!.length; k++) {
      const pa = paths[i]![k]!, pb = paths[j]![k]!;
      if (!pa.airborne || !pb.airborne) continue;
      const independent = (airport.xAtc.independentApproaches ?? []).some(([x, y]) => (pa.runway === x && pb.runway === y) || (pa.runway === y && pb.runway === x));
      if (Math.abs(pa.alt - pb.alt) >= VERTICAL_MIN_FT) continue;
      const de = pb.e - pa.e, dn = pb.n - pa.n, distance = Math.hypot(de, dn) / NM_TO_M;
      let conflict = distance < LATERAL_MIN_NM && !independent;
      if (!conflict && opts.wakeSeparation && distance < 6) {
        for (const [lead, follow, lp, fp] of [[a, b, pa, pb], [b, a, pb, pa]] as const) {
          const need = WAKE_NM[lead.category][follow.category];
          const bearing = Math.atan2(lp.e - fp.e, lp.n - fp.n) * 180 / Math.PI;
          if (need && distance < need && fp.alt <= lp.alt + 100 && Math.abs(wrap180(bearing - fp.heading)) < 30 && Math.abs(wrap180(lp.heading - fp.heading)) < 45) conflict = true;
        }
      }
      if (conflict) { out.push({ a, b, key: a.id < b.id ? `${a.id}-${b.id}` : `${b.id}-${a.id}`, inS: (k + 1) * 5 }); break; }
    }
  }
  return out;
}
